import { PersonSearchRequest, PersonProperty } from 'app/api/roster';
import { GUID } from 'shared/person-search-field/regexes';

/**
 * Attempts to assemble search requests in a format suitable for Roster's person
 * searching. The search query can be a user's GUID, first name, last name, or
 * their full name.
 * 
 * @param searchText - The search query.
 * @param param1 - Properties to allow potentially sort by (?).
 */
export function buildPersonSearchRequests(
    searchText: string,
    {
        personProperties = ['groups' as PersonProperty],
    }: {
        personProperties?: PersonProperty[];
    } = {},
): PersonSearchRequest[] {
    // If you don't search for anything, you get the first page of results
    if (!searchText) {
        return [{ personProperties }];
    }

    // Allow for a search by user GUID
    if (searchText.trim().match(GUID)) {
        return [{ personProperties, personId: searchText.trim() }];
    }

    // Separate the search query by spaces while removing any potential whitespace on
    // the ends or in the string. Also, since Roster's search doesn't support a search
    // with a 1 character long token, we are filtering these out for now. Not ideal, but
    // this will work.
    const segments = searchText.trim().split(/\s+/).filter(t => t.length >= 2);
    
    // Build search requests for the full, trimmed search query. Since first and last
    // names are stored in two different fields in Roster, and we won't know which one
    // the user is trying to search by, we have to attempt to query both.
    const requests: PersonSearchRequest[] = [
        { personProperties, firstName: segments.join(' ') },
        { personProperties, lastName: segments.join(' ') },
    ];

    // Worse, they might be typing in a *full* name. And since some people have multiple
    // first or last names, we need to try drawing the "line" between each pair of words.
    // Ultimately, we should handle this inside Roster instead, but for now this is our
    // best effort.
    for (let i = 1; i < segments.length; i++) {
        requests.push({
            personProperties,
            firstName: segments.slice(0, i).join(' '),
            lastName: segments.slice(i).join(' '),
        });
    }

    return requests;
}
